import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  List,
  Select,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { GoBackButton } from "../login/components/GoBackButton";
import { createVtuCustomerComplaint } from "./services";
import {
  errorNotifier,
  successNotifier,
} from "../../components/NotificationHandler";

export const CustomerCareView = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    reason: "",
    description: "",
  });

  const createComplaintMutation = useMutation({
    mutationFn: createVtuCustomerComplaint,
  });

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.description.trim()
    ) {
      errorNotifier("Name, email and description are required");
      return;
    }

    try {
      await createComplaintMutation.mutateAsync({
        name: formData.name.trim(),
        email: formData.email.trim(),
        description: formData.description.trim(),
        reason: formData.reason,
      });

      successNotifier("Complaint submitted successfully");
      setFormData({
        name: "",
        email: "",
        reason: "",
        description: "",
      });
    } catch (error) {
      errorNotifier(
        error?.response?.data?.data ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit complaint",
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          d: "flex",
          alignItems: "start",
          gap: "16px",
          mb: "24px",
        }}
      >
        <Box sx={{ pt: "2" }}>
          <GoBackButton />
        </Box>
        <Box sx={{ d: "flex", flexDirection: "column", gap: "8px" }}>
          <Text fontSize="24px" fontWeight="700" color="primary.500">
            Contact Us Now
          </Text>
          <Text>{new Date()?.toLocaleDateString()}</Text>
        </Box>
      </Box>

      <Box
        as="form"
        onSubmit={handleSubmit}
        borderRadius="default"
        p="20px"
        bg="white"
        boxShadow={"lg"}
      >
        <Box margin={"auto"} w={["100%", "80%", "60%"]}>
          <Text fontSize="20px" fontWeight="700" color="primary.500" mb="16px">
            Customer Care
          </Text>

          <VStack spacing="14px" align="stretch">
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(event) => handleChange("name", event.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(event) => handleChange("email", event.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Reason</FormLabel>
              <Select
                placeholder="Select reason"
                value={formData.reason}
                onChange={(event) => handleChange("reason", event.target.value)}
              >
                <option value="failed-transaction">Failed Transaction</option>
                <option value="wrong-deduction">Wrong Deduction</option>
                <option value="service-delay">Service Delay</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Describe your issue"
                minH="140px"
                value={formData.description}
                onChange={(event) =>
                  handleChange("description", event.target.value)
                }
              />
            </FormControl>

            <VStack pt="6px">
              <List spacing="8px" color="gray.600" fontSize="13px">
                <Text as="li">
                  1. Ensure the email you are providing is active and valid.
                </Text>
                <Text as="li">
                  2. Please wait for a period of 24 hours before sending another
                  as sending multiple messages will reduce the response time as
                  we try to resolve.
                </Text>
                <Text as={"li"} fontSize="13px" color="gray.600">
                  3. Kindly check your inbox and/or spam for our response.
                </Text>{" "}
              </List>
            </VStack>
            <Checkbox alignSelf="flex-start" colorScheme="primary" size="md">
              <Text color="gray.500" fontSize="12px" lineHeight="1.5">
                By submitting this form, you consent to receive customer support
                text messages from Ovend at the number provided. Msg & data
                rates may apply. Message frequency varies. Reply STOP to
                unsubscribe or HELP for support. View our Privacy Policy and
                Terms.
              </Text>
            </Checkbox>

            <Button
              w={"full"}
              type="submit"
              alignSelf="flex-start"
              px="24px"
              isLoading={createComplaintMutation.isPending}
            >
              Send Message
            </Button>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};
